import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TableCell,
  Tooltip,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { makeStyles } from '@material-ui/core/styles';
import { InfoOutlined } from '@material-ui/icons';
import { APP_CONSTANTS } from '../../constants';
import { AppContainer } from '../../packages/AppContainer';
import AppPageTitle from '../../packages/AppPageTitle';
import { AppButton } from '../../packages/AppButton';
import AppTable from '../../packages/AppTable';
import IOSSwitch from '../../packages/AppIOSSwitch';
import {
  getAgenciesAvailableForSelectedProductSubscription,
  subscribeProductForAgency,
  unsubscribeProductForAgency,
  updateAgencyPreferences,
  updateProductForAgency,
} from '../../modules/Agency/actions';
import { StyledDialogActions } from '../../packages/AppStyledComponents';
import AvailabilitySlotPreferences from '../../components/AvailabilitySlotPreferences';
import { useCalendlyOAuth } from '../../hooks/useCalendlyOAuth';

const useStyles = makeStyles(() => ({
  dialogTitle: {
    padding: '8px',
  },

  prefrencesContainer: {
    padding: '10px',
    boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
    borderRadius: '20px',
  },

  infoIcon: {
    color: '#343A40',
    opacity: 0.9,
  },
  infoButton: {
    padding: '0px',
    cursor: 'pointer',
  },
  tooltip: {
    fontSize: '18px',
  },
}));

export default function AgenciesProductAccess() {
  const [agencyList, setAgencyList] = useState([]);
  const [isPrefrencesDialogOpen, setIsPrefrencesDialogOpen] = useState(false);
  const [selectedAgencyDetails, setSelectedAgencyDetails] = useState({});
  const [errors, setErrors] = useState({});
  const [isDataAvailable, setIsDataAvailable] = useState({});
  const classes = useStyles();
  const params = useParams();
  const { productId } = params;
  const userType = useSelector((state) => state.User.userType);
  const history = useHistory();
  const dispatch = useDispatch();

  const calendlyOAuth = useCalendlyOAuth(
    selectedAgencyDetails?.agency_id,
    selectedAgencyDetails?.agency_preferences?.calendlyConnected || false,
  );
  const TABLE_HEADER_AGENCY_ADMIN = [
    {
      label: 'agency_name',
      key: 'agency_name',
      search: true,
      sort: true,
    },
    {
      label: 'Activation date',
      key: 'activationDate',
      search: false,
      sort: true,
      width: '120px',
    },
    {
      label: 'Activation Status',
      key: 'subscribed',
      search: false,
      sort: true,
      width: '40px',
    },
  ];

  const TABLE_HEADER_SUPER_ADMIN = [
    {
      label: 'agency_name',
      key: 'agency_name',
      search: true,
      sort: true,
    },
    {
      label: 'Activation date',
      key: 'activationDate',
      search: false,
      sort: true,
      width: '120px',
    },
    {
      label: 'Activation Status',
      key: 'subscribed',
      search: false,
      sort: true,
      width: '40px',
    },
    {
      label: 'IN PILOT',
      key: 'inPilot',
      search: false,
      sort: true,
      width: '200px',
    },
  ];

  const PRODUCT_ID_MAPPING = {
    ENGAGE: 'Engage',
    SCHEDULE: 'Schedule+',
    NURTURE: 'Nurture',
    AGENT_Q: 'Agent Q',
    ANSWER: 'Answer',
    BOOST: 'Boost',
  };

  useEffect(() => {
    if (productId) {
      getAvailableAgenciesList(productId);
    }
  }, [productId]);

  const getAvailableAgenciesList = (product_Id) => {
    dispatch(
      getAgenciesAvailableForSelectedProductSubscription(product_Id),
    ).then((resp) => {
      setAgencyList(resp?.data);
    });
  };

  const onToggleStatusChange = (value, agencyDetails) => {
    switch (productId) {
      case 'SCHEDULE':
        if (agencyDetails?.subscribed) {
          unsubscribeAgency(agencyDetails?.agency_id);
        } else {
          setSelectedAgencyDetails(agencyDetails);
          setIsPrefrencesDialogOpen(true);
        }
        break;
      default:
        console.error('NO PRODUCT ID FOUND TO PERFORM ACTION');
    }
  };

  const onEditButtonClick = (agencyDetails) => {
    switch (productId) {
      case 'SCHEDULE':
        setSelectedAgencyDetails({ ...agencyDetails, actionType: 'edit' });
        setIsPrefrencesDialogOpen(true);
        break;
      default:
        console.error('NO PRODUCT ID FOUND TO PERFORM ACTION');
    }
  };

  const onInPilotToggleStatusChange = (value, agencyDetails) => {
    switch (productId) {
      case 'SCHEDULE':
        if (value) {
          updateProductInPilot(true, agencyDetails?.agency_id);
        } else {
          updateProductInPilot(false, agencyDetails?.agency_id);
        }
        break;
      default:
        console.error('NO PRODUCT ID FOUND TO PERFORM ACTION');
    }
  };

  const updateProductInPilot = (inPilot, agencyId) => {
    const formData = { inPilot, agencyId };
    dispatch(updateProductForAgency(formData, productId)).then(() => {
      getAvailableAgenciesList(productId);
    });
  };

  const unsubscribeAgency = (agencyId) => {
    const formData = { agencyId };
    dispatch(unsubscribeProductForAgency(formData, productId)).then(() => {
      getAvailableAgenciesList(productId);
    });
  };

  const onModalSubmitClick = () => {
    const formData = JSON.parse(
      JSON.stringify({
        ...selectedAgencyDetails,
        agencyId: selectedAgencyDetails?.agency_id,
      }),
    );
    const isEditMode = formData?.actionType === 'edit';

    const keysToBeDeleted = ['agency_name', 'subscribed', 'agency_id'];
    keysToBeDeleted.forEach((key) => {
      delete formData[key];
    });

    if (isEditMode) {
      const keysToBeDeletedInEdit = [
        'activationDate',
        'agencyId',
        'inPilot',
        'actionType',
      ];

      keysToBeDeletedInEdit.forEach((key) => {
        delete formData[key];
      });

      dispatch(
        updateAgencyPreferences(selectedAgencyDetails?.agency_id, formData),
      ).then(() => {
        setIsPrefrencesDialogOpen(false);
        getAvailableAgenciesList(productId);
      });
    } else {
      dispatch(subscribeProductForAgency(formData, productId)).then(() => {
        setIsPrefrencesDialogOpen(false);
        getAvailableAgenciesList(productId);
      });
    }
  };

  const onModalContentChange = (value, error, page, availableStatus) => {
    const prevError = errors?.[page] || false;
    if (prevError !== error) {
      setErrors({ ...errors, [page]: error });
    }

    const prevAvailableStatus = isDataAvailable?.[page] || false;
    if (prevAvailableStatus !== availableStatus) {
      setIsDataAvailable({ ...isDataAvailable, [page]: availableStatus });
    }

    if (value) {
      setSelectedAgencyDetails({ ...selectedAgencyDetails, ...value });
    }
  };

  return useMemo(
    () => (
      <>
        <AppContainer>
          <Grid
            alignItems="flex-start"
            justifyContent="center"
            alignContent="stretch"
            spacing={2}
            container
          >
            <Grid item xs={12}>
              <AppPageTitle
                title={`${(
                  PRODUCT_ID_MAPPING?.[productId] || productId
                )?.toUpperCase()} Activation`}
              >
                <Grid
                  alignItems="flex-start"
                  justify="space-between"
                  alignContent="stretch"
                  spacing={2}
                  container
                >
                  <Grid item>
                    <AppButton
                      variant="text"
                      color="primary"
                      label="Back to Products"
                      handleClick={() => {
                        history.push(APP_CONSTANTS.APP_ROUTE_URLS.PRODUCTS);
                      }}
                    />
                  </Grid>
                </Grid>
              </AppPageTitle>
            </Grid>
            <Grid item xl={6} lg={6} md={6} xs={12}>
              {userType === 'BOOMERS_ADMIN' && (
                <AppTable
                  tableName="product-accessible-agency-list-table"
                  size="small"
                  orderBy=""
                  searchCol
                  data={agencyList}
                  headers={TABLE_HEADER_SUPER_ADMIN}
                  rowsPerPageOptions={[500, 1000, 1500]}
                  enableCellRender
                  render={{
                    subscribed: (props) => (
                      <TableCell
                        align="center"
                        size="small"
                        style={{
                          width: '40px',
                        }}
                      >
                        <IOSSwitch
                          checked={props?.row?.subscribed || false}
                          onChange={(event) => {
                            onToggleStatusChange(
                              event?.target?.checked,
                              props.row,
                            );
                          }}
                          name="agency_subscription"
                        />
                      </TableCell>
                    ),
                    inPilot: (props) => (
                      <TableCell
                        align="center"
                        size="small"
                        style={{
                          width: '150px',
                        }}
                      >
                        <IOSSwitch
                          checked={props?.row?.inPilot || false}
                          disabled={!props?.row?.subscribed || false}
                          onChange={(event) => {
                            onInPilotToggleStatusChange(
                              event?.target?.checked,
                              props.row,
                            );
                          }}
                          name="product_inPilot"
                        />
                        <Tooltip
                          classes={{ tooltip: classes.tooltip }}
                          style={{
                            marginLeft: '16px',
                          }}
                          title="Edit Product Preferences"
                          arrow
                        >
                          <IconButton
                            style={{
                              visibility: props?.row?.subscribed
                                ? 'visible'
                                : 'hidden',
                              marginLeft: '16px',
                            }}
                            size="small"
                            color="secondary"
                            tooltip="Edit Product Preferences"
                            onClick={() => {
                              onEditButtonClick(props.row);
                            }}
                          >
                            <EditOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    ),
                  }}
                />
              )}
              {userType === 'AGENCY_ADMIN_BASIC' && (
                <AppTable
                  tableName="product-accessible-agency-list-table"
                  size="small"
                  orderBy=""
                  searchCol
                  data={agencyList}
                  headers={TABLE_HEADER_AGENCY_ADMIN}
                  rowsPerPageOptions={[500, 1000, 1500]}
                  enableCellRender
                  render={{
                    subscribed: (props) => (
                      <TableCell
                        align="center"
                        size="small"
                        style={{
                          width: '40px',
                        }}
                      >
                        <IOSSwitch
                          checked={props?.row?.subscribed || false}
                          disabled={props?.row?.subscribed || false}
                          onChange={(event) => {
                            onToggleStatusChange(
                              event?.target?.checked,
                              props.row,
                            );
                          }}
                          name="agency_subscription"
                        />
                        <IconButton
                          style={{
                            visibility: props?.row?.subscribed
                              ? 'visible'
                              : 'hidden',
                            marginLeft: '16px',
                          }}
                          size="small"
                          color="secondary"
                          onClick={() => {
                            onEditButtonClick(props.row);
                          }}
                        >
                          <EditOutlinedIcon />
                        </IconButton>

                        <Tooltip
                          classes={{ tooltip: classes.tooltip }}
                          style={{
                            visibility: props?.row?.subscribed
                              ? 'visible'
                              : 'hidden',
                            marginLeft: '16px',
                          }}
                          title="Contact support@aidquest.com for deactivating the product. Deactivation can only occur at the end of monthly billing cycles."
                          arrow
                        >
                          <IconButton className={classes?.infoButton}>
                            <InfoOutlined className={classes?.infoIcon} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    ),
                  }}
                />
              )}
            </Grid>
          </Grid>
        </AppContainer>

        <Dialog
          open={isPrefrencesDialogOpen}
          scroll="paper"
          onClose={() => {}}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle className={classes?.dialogTitle}></DialogTitle>
          <DialogContent>
            <div className={classes?.prefrencesContainer}>
              <AvailabilitySlotPreferences
                apiFormData={{
                  timeFrames:
                    selectedAgencyDetails?.clientBookingPreferences || [],
                  radioAndFieldData: {
                    clientAppointmentTerm:
                      selectedAgencyDetails?.clientAppointmentTerm,
                    clientSchedulingCalender:
                      selectedAgencyDetails?.clientSchedulingCalender || '',
                    calendlyClientAppointmentUrl:
                      selectedAgencyDetails?.calendlyClientAppointmentUrl,
                  },
                }}
                pageType="appointment_booking"
                handleChange={onModalContentChange}
                formDataKey="clientBookingPreferences"
                calendlyOAuth={calendlyOAuth}
              ></AvailabilitySlotPreferences>
            </div>

            <Box style={{ marginTop: '20px' }}></Box>

            <div className={classes?.prefrencesContainer}>
              <AvailabilitySlotPreferences
                apiFormData={{
                  timeFrames:
                    selectedAgencyDetails?.employeeInterviewBookingPreferences ||
                    [],
                  radioAndFieldData: {
                    caregiverInterviewTerm:
                      selectedAgencyDetails?.caregiverInterviewTerm,
                    caregiverSchedulingCalender:
                      selectedAgencyDetails?.caregiverSchedulingCalender || '',
                    calendlyCaregiverInterviewUrl:
                      selectedAgencyDetails?.calendlyCaregiverInterviewUrl,
                  },
                }}
                pageType="interview_booking"
                handleChange={onModalContentChange}
                formDataKey="employeeInterviewBookingPreferences"
                calendlyOAuth={calendlyOAuth}
              ></AvailabilitySlotPreferences>
            </div>
          </DialogContent>
          <StyledDialogActions>
            <AppButton
              size="small"
              label="Cancel"
              variant="contained"
              handleClick={() => {
                setIsPrefrencesDialogOpen(false);
              }}
            />
            <AppButton
              size="small"
              color="primary"
              label="Ok"
              disabled={
                Object.values(errors)?.includes(true) ||
                !Object.values(isDataAvailable)?.includes(true)
              }
              variant="contained"
              handleClick={() => {
                onModalSubmitClick();
              }}
            />
          </StyledDialogActions>
        </Dialog>
      </>
    ),
    [
      agencyList,
      isPrefrencesDialogOpen,
      selectedAgencyDetails,
      isDataAvailable,
      errors,
    ],
  );
}
